import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { AssetCard } from "@/components/assets/asset-card";
import { CheckInForm } from "@/components/assets/check-in-form";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  QrCode,
  Clock,
  User,
  LogOut,
  LogIn,
  Image as ImageIcon,
} from "lucide-react";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/assets/$assetId")({
  component: AssetDetailPage,
});

interface Asset {
  id: string;
  name: string;
  assetType: string;
  serialNumber: string | null;
  tagNumber: string | null;
  location: string | null;
  status: string;
  currentAssigneeUserId: string | null;
  inspectionIntervalDays: number | null;
  inspectionTemplateId: string | null;
  inspectionTemplateName: string | null;
  imageFileId: string | null;
  lastInspectedAt: string | null;
  nextInspectionDueAt: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AssignmentRecord {
  assignment: {
    id: string;
    assetId: string;
    userId: string;
    flhaId: string | null;
    checkedOutAt: string;
    checkedInAt: string | null;
    conditionOut: string;
    conditionIn: string | null;
    checkInNotes: string | null;
    checkInPhotoFileId: string | null;
  };
  userName: string;
}

const CONDITION_COLORS: Record<string, string> = {
  good: "bg-green-100 text-green-800",
  fair: "bg-yellow-100 text-yellow-800",
  poor: "bg-red-100 text-red-800",
  damaged: "bg-yellow-100 text-yellow-800",
  needs_service: "bg-red-100 text-red-800",
};

function AssetDetailPage() {
  const { assetId } = Route.useParams();
  const { user } = Route.useRouteContext() as { user: { id: string } };
  const navigate = useNavigate();
  const goBack = useGoBack({ to: "/assets" });
  const queryClient = useQueryClient();
  const [showCheckIn, setShowCheckIn] = useState(false);

  // Fetch asset detail
  const { data: assetData, isLoading: assetLoading } = useQuery({
    queryKey: ["asset", assetId],
    queryFn: () => apiGet<{ data: Asset }>(`/api/assets/${assetId}`),
  });

  // Fetch assignment history
  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ["asset-assignments", assetId],
    queryFn: () =>
      apiGet<{ data: AssignmentRecord[]; total: number }>(
        `/api/assets/${assetId}/assignments?limit=10`
      ),
  });

  // Fetch worker's active checkouts to determine if user has this asset checked out
  const { data: myCheckoutsData } = useQuery({
    queryKey: ["my-checkouts"],
    queryFn: () =>
      apiGet<{
        data: Array<{
          assignment: { id: string; assetId: string; checkedInAt: string | null };
        }>;
      }>("/api/assets/my-checkouts"),
  });

  const asset = assetData?.data;
  const assignments = assignmentsData?.data || [];
  const myCheckouts = myCheckoutsData?.data || [];
  const myActiveCheckout = myCheckouts.find(
    (c) => c.assignment.assetId === assetId && !c.assignment.checkedInAt
  );

  // Get most recent completed assignment for check-in photo
  const lastCompletedAssignment = assignments.find(
    (a) => a.assignment.checkedInAt !== null
  );

  // Fetch check-in photo presigned URL if available
  const { data: checkInPhotoData } = useQuery({
    queryKey: [
      "file-url",
      lastCompletedAssignment?.assignment.checkInPhotoFileId,
    ],
    queryFn: () =>
      apiGet<{ url: string }>(
        `/api/files/${lastCompletedAssignment!.assignment.checkInPhotoFileId}/url`
      ),
    enabled: !!lastCompletedAssignment?.assignment.checkInPhotoFileId,
    staleTime: 5 * 60 * 1000,
  });

  const handleCheckInComplete = () => {
    setShowCheckIn(false);
    queryClient.invalidateQueries({ queryKey: ["asset", assetId] });
    queryClient.invalidateQueries({ queryKey: ["asset-assignments", assetId] });
    queryClient.invalidateQueries({ queryKey: ["my-checkouts"] });
  };

  if (assetLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-4">
        <Button variant="ghost" size="sm" className="gap-1" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Asset not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show check-in form if active
  if (showCheckIn && myActiveCheckout) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={() => setShowCheckIn(false)}
        >
          <ArrowLeft className="h-4 w-4" />
          Cancel
        </Button>
        <CheckInForm
          asset={asset}
          assignmentId={myActiveCheckout.assignment.id}
          onComplete={handleCheckInComplete}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Back nav */}
      <div className="flex items-center gap-2 pt-2">
        <Button variant="ghost" size="sm" className="gap-1" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Asset detail card */}
      <AssetCard asset={asset} />

      {/* Action buttons */}
      <div className="flex gap-2">
        {myActiveCheckout ? (
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2"
            size="lg"
            onClick={() => setShowCheckIn(true)}
          >
            <LogIn className="h-4 w-4" />
            Check In
          </Button>
        ) : asset.status === "available" ? (
          <Link
            to="/assets/$assetId/checkout"
            params={{ assetId: asset.id }}
            className="flex-1"
          >
            <Button className="w-full bg-paris-red hover:bg-paris-red-dark gap-2" size="lg">
              <LogOut className="h-4 w-4" />
              Check Out
            </Button>
          </Link>
        ) : null}
      </div>

      {/* Last check-in condition photo (ASSET-06) */}
      {lastCompletedAssignment?.assignment.checkInPhotoFileId && (
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                Last Check-in Condition Photo
              </span>
            </div>
            {checkInPhotoData?.url ? (
              <img
                src={checkInPhotoData.url}
                alt="Last check-in condition"
                className="w-full rounded-lg object-cover max-h-48"
              />
            ) : (
              <Skeleton className="h-32 w-full rounded-lg" />
            )}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-muted-foreground">Condition:</span>
              <Badge
                className={cn(
                  "text-xs px-1.5 py-0",
                  CONDITION_COLORS[
                    lastCompletedAssignment.assignment.conditionIn || "good"
                  ]
                )}
              >
                {(
                  lastCompletedAssignment.assignment.conditionIn || "good"
                ).replace("_", " ")}
              </Badge>
              <span className="text-xs text-muted-foreground">
                by {lastCompletedAssignment.userName}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignment History */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Assignment History
        </h2>

        {assignmentsLoading && (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        )}

        {!assignmentsLoading && assignments.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No assignment history yet
              </p>
            </CardContent>
          </Card>
        )}

        {!assignmentsLoading &&
          assignments.map((record) => (
            <Card key={record.assignment.id}>
              <CardContent className="py-3 px-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium truncate">
                        {record.userName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>
                        Out:{" "}
                        {new Date(
                          record.assignment.checkedOutAt
                        ).toLocaleDateString()}
                      </span>
                      {record.assignment.checkedInAt && (
                        <span>
                          In:{" "}
                          {new Date(
                            record.assignment.checkedInAt
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {record.assignment.conditionOut && (
                      <Badge
                        variant="outline"
                        className="text-xs px-1 py-0"
                      >
                        Out: {record.assignment.conditionOut}
                      </Badge>
                    )}
                    {record.assignment.conditionIn && (
                      <Badge
                        className={cn(
                          "text-xs px-1 py-0",
                          CONDITION_COLORS[record.assignment.conditionIn]
                        )}
                      >
                        In: {record.assignment.conditionIn.replace("_", " ")}
                      </Badge>
                    )}
                    {!record.assignment.checkedInAt && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs px-1 py-0">
                        Active
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
}
